import { bookingRefundOffers } from "../../../app/api/cancelBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import {
  getRecoilStateValue,
  updateRecoilState,
} from "../../../Orchestrator/Recoils/Events/Recoil";
import type { IRefundQuoteEntry } from "../../../Orchestrator/Recoils/AddonsAtoms";
import { AncillaryStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../../Orchestrator/Types/Index";

type IResult = { status: "success" | "fail"; message?: string };

/**
 * Speculatively POSTs /bookings/{id}/refund-offers at manage-seating entry
 * with every FULFILLED ancillary id in the booking, and caches the per-id
 * refundFee + refundableAmount in refundOffersByFulfillmentIdAtom.
 *
 * Display-only under the new server-side refund pipeline: AddonsModal
 * reads the cache to show accurate per-meal refund values before payment,
 * instead of approximating with `removedBaselineTotal − bookingWideModFee`.
 * Actual refund settlement no longer flows through this endpoint — it
 * happens at gateway-session settlement via the `refundedAncillaries`
 * field of POST /api/payment/session. The cache exists purely for the UI.
 *
 * Non-fatal: a missing/stale cache just makes the modal fall back to the
 * naive math; the pay-time refund still settles correctly because the
 * backend doesn't depend on these cached refundOfferIds.
 *
 * Mapping note: the pay-time path stores ancillary `id`s in the
 * `fulfillmentIds` field of pendingRefundFulfillmentIdsAtom and forwards
 * them as-is to the session POST, which works because the backend accepts
 * ancillary ids as fulfillment ids on these endpoints. We follow the same
 * convention here: the cache is keyed by ancillary id.
 */
export class ProbeRefundOffersAction extends BaseAction<IResult, void> {
  protected override deduplicate = true;

  override async run(): Promise<IResult> {
    const bookingResponse = getRecoilStateValue<IBookingResponse>(
      "bookingResponseAtom",
    );
    const bookingId = bookingResponse?.booking?.id || "";
    if (!bookingId) {
      return { status: "fail", message: "Missing bookingId" };
    }

    const fulfillmentIds: string[] = [];
    for (const offer of bookingResponse?.booking?.bookedOffers || []) {
      for (const anc of (offer as any).ancillaries || []) {
        if (
          (anc as any)?.status === AncillaryStatusEnum.FULFILLED &&
          (anc as any)?.id
        ) {
          fulfillmentIds.push((anc as any).id);
        }
      }
    }

    if (fulfillmentIds.length === 0) {
      // No FULFILLED ancillaries — clear any stale cache and bail. Don't
      // POST: OSDM rejects empty fulfillmentIds with 400.
      updateRecoilState("refundOffersByFulfillmentIdAtom", null);
      return { status: "success" };
    }

    try {
      const refundReq = {
        fulfillmentIds,
        overruleCode: null,
        refundDate: new Date().toISOString(),
      };
      const response: any = await bookingRefundOffers(refundReq, bookingId);
      const ok = response?.code === 200 || response?.status === "ok";
      if (!ok) {
        console.warn("[ProbeRefundOffersAction] POST /refund-offers failed:", {
          status: response?.status,
          code: response?.code,
          errorCode:
            response?.errorCode ?? response?.answer?.errorCode,
          traceId: response?.answer?.traceId,
        });
        return { status: "fail", message: "POST failed" };
      }

      const refundOffers: any[] = response?.answer?.refundOffers || [];
      const entries: Record<string, IRefundQuoteEntry> = {};
      for (const offer of refundOffers) {
        const offerId: string = offer?.id || "";
        if (!offerId) continue;
        const aggregateFee = calculatePrice(offer?.refundFee) || 0;
        const aggregateAmount = calculatePrice(offer?.refundableAmount) || 0;
        const fulfillments: any[] = offer?.fulfillments || [];

        // OSDM groups multiple ancillaries under one refund offer when the
        // request bundles them. Per-meal values live in
        // `refundOfferBreakDown[]` — each item has its own refundFee +
        // refundableAmount and a `bookingParts[]` array referencing the
        // fulfillment(s) it covers. Fall back to even-split of the aggregate
        // when the breakdown is missing/sparse, and to the aggregate-as-each
        // (legacy behaviour) when even fulfillments are missing.
        const breakdown: any[] = offer?.refundOfferBreakDown || [];
        const stampedFulfillmentIds = new Set<string>();
        for (const item of breakdown) {
          const itemFee = calculatePrice(item?.refundFee) || 0;
          const itemAmount = calculatePrice(item?.refundableAmount) || 0;
          const parts: any[] = item?.bookingParts || [];
          for (const part of parts) {
            const fid: string = part?.id || "";
            if (!fid) continue;
            entries[fid] = {
              refundOfferId: offerId,
              refundFee: itemFee,
              refundableAmount: itemAmount,
            };
            stampedFulfillmentIds.add(fid);
          }
        }

        // Cover any fulfillments the breakdown didn't reach — divide the
        // aggregate evenly so the per-meal display stays proportionate when
        // the backend returns an aggregate-only offer.
        const remaining = fulfillments.filter((f) => {
          const fid: string = f?.id || "";
          return fid && !stampedFulfillmentIds.has(fid);
        });
        if (remaining.length > 0) {
          const splitFee = aggregateFee / remaining.length;
          const splitAmount = aggregateAmount / remaining.length;
          for (const f of remaining) {
            const fid: string = f?.id || "";
            if (!fid) continue;
            entries[fid] = {
              refundOfferId: offerId,
              refundFee: splitFee,
              refundableAmount: splitAmount,
            };
          }
        }
      }

      updateRecoilState("refundOffersByFulfillmentIdAtom", {
        bookingId,
        entries,
      });
      return { status: "success" };
    } catch (error: any) {
      console.warn(
        "[ProbeRefundOffersAction] probe failed (non-fatal):",
        error?.message,
      );
      return { status: "fail", message: error?.message };
    }
  }
}

export const probeRefundOffersAction = new ProbeRefundOffersAction();
